<script setup lang="ts">
import {computed, ref, watch} from "vue";
import {Pencil, Sparkles, Stethoscope} from "@lucide/vue";
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from "@/core/components/ui/dialog";
import {Textarea} from "@/core/components/ui/textarea";
import BaseButton from "@/core/components/base/BaseButton.vue";
import DosageForm from "@/dosage/components/DosageForm.vue";
import {DosageFormData} from "@/dosage/interfaces/DosageForm";
import DosageFormService from "@/dosage/services/DosageFormService";
import TreatmentLineDosageService from "@/medication-checkup/services/TreatmentLineDosageService";
import {DosageDialogLine, TreatmentLineDosageDraft} from "@/medication-checkup/interfaces/treatment-selections";
import MedicationCheckupPrescriptionPane from "@/medication-checkup/components/MedicationCheckupPrescriptionPane.vue";
import {TreatmentSheet} from "@/medication-checkup/services/TreatmentSheets";

/**
 * The structured posology of a treatment line, edited on the shared dosage
 * form and handed back to the scene as a draft: nothing is written here, the
 * closing action of the collection carries every draft with the selection.
 *
 * The collection describes what the patient takes today, so the form hides
 * the period and the custom dates; the duration and conditions go in words.
 * Handed the prescriptions of the line, the dialog shows them beside the
 * form, so the posology is entered with the document in sight.
 */

const props = defineProps<{
    /**
     * The line whose posology is edited — null closes the dialog.
     */
    line: DosageDialogLine | null;
    /**
     * The draft the scene already holds for the line, if any.
     */
    draft: TreatmentLineDosageDraft | null;
    /**
     * What the posology is entered from, in words — on the plan of the
     * advice interview, what the doctor retained.
     */
    instruction?: string | null;
    /**
     * The scanned prescriptions behind the line, most recent first — none
     * keeps the dialog to the form alone.
     */
    sheets?: TreatmentSheet[];
}>();

const emit = defineEmits<{
    close: [];
    save: [draft: TreatmentLineDosageDraft];
}>();

const form = ref<InstanceType<typeof DosageForm> | null>(null);
const formData = ref<DosageFormData>(DosageFormService.defaultFormValue());
const comment = ref('');

/**
 * The form edits a copy of the draft: the scene's draft — or the served
 * dosage it derives from — only changes when the dialog saves, so closing
 * on an error leaves the line as it was.
 */
/**
 * The line and the instruction the dialog shows. They outlive the closing:
 * the content is still played out while the dialog animates away, and
 * emptying it under the animation unmounts what reka is still patching.
 */
const shown = ref<DosageDialogLine | null>(null);

const shownInstruction = ref<string | null>(null);

const shownSheets = ref<TreatmentSheet[]>([]);

watch(() => props.line, (line) => {
    if (!line) {
        return;
    }

    shown.value = line;
    shownInstruction.value = props.instruction ?? null;
    shownSheets.value = props.sheets ?? [];

    const draft = TreatmentLineDosageService.clone(props.draft ?? TreatmentLineDosageService.draftOf(line));

    formData.value = draft.data;
    comment.value = draft.comment;
});

/**
 * The posology as the line will read it, composed as the form goes.
 */
const preview = computed(() => TreatmentLineDosageService.describe(formData.value, comment.value));

/**
 * The proposal the form opened on, when the line carries one and nothing
 * was recorded or drafted yet: the pharmacist reads it against the
 * prescription, then saving is accepting.
 */
const proposal = computed(() =>
    shown.value && !shown.value.dosage && !props.draft ? shown.value.dosage_proposal : null
);

/**
 * A form opened on a proposal shows at once what the reading left out — a
 * dose, a take — rather than on saving: the proposal is the pharmacist's to
 * complete. The form mounts anew with every line the dialog opens on.
 */
watch(form, mounted => {
    if (mounted && proposal.value) {
        mounted.validate();
    }
});

const confidenceLabel = computed(() =>
    proposal.value ? TreatmentLineDosageService.confidenceLabel(proposal.value.confidence) : ''
);

const save = () => {
    if (!form.value?.validate()) {
        return;
    }

    emit('save', {data: formData.value, comment: comment.value});
};
</script>

<template>
  <Dialog :open="line !== null" @update:open="open => !open && emit('close')">
    <DialogContent
      class="max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden"
      :class="shownSheets.length > 0 ? 'h-[90vh] w-[95vw] max-w-[1240px]' : 'max-w-3xl'"
      @interact-outside.prevent
      @open-auto-focus.prevent
    >
      <DialogHeader class="flex-row items-center gap-3 px-5 py-4 pr-12 border-b border-gray-light space-y-0">
        <Pencil class="size-5 shrink-0 text-primary" />
        <div class="text-start">
          <DialogTitle class="text-base text-primary">
            Modifier la posologie
          </DialogTitle>
          <DialogDescription class="text-sm">
            {{ shown?.label }}
          </DialogDescription>
        </div>
      </DialogHeader>

      <div class="flex flex-1 min-h-0">
        <div
          v-if="shownSheets.length > 0 && shown"
          class="flex w-[46%] shrink-0 flex-col border-r border-solid border-gray-light bg-gray-100 p-4"
        >
          <MedicationCheckupPrescriptionPane :key="shown.id" :sheets="shownSheets" class="flex-1" />
        </div>

        <div class="flex-1 min-h-0 overflow-y-auto bg-gray-100 p-4 space-y-4">
          <div
            v-if="proposal"
            class="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-100 p-3 text-xs text-amber-800"
          >
            <Sparkles class="size-4 shrink-0 mt-0.5" />
            <div>
              <span class="font-semibold">Posologie proposée par l'IA Apodis</span>,
              lue sur l'ordonnance de {{ proposal.prescriber.name ?? 'prescripteur inconnu' }}
              ({{ confidenceLabel }}) — à vérifier avant d'enregistrer.
              <span v-if="proposal.evidence" class="block mt-1 italic text-amber-700">
                « {{ proposal.evidence }} »
              </span>
            </div>
          </div>

          <div
            v-if="shownInstruction"
            class="flex items-start gap-2 rounded-lg bg-lightPurple-lightest border border-lightPurple p-3 text-xs text-primary-dark"
          >
            <Stethoscope class="size-4 shrink-0 mt-0.5" />
            <div>
              <span class="font-semibold">Retenu par le médecin traitant</span> : {{ shownInstruction }}
            </div>
          </div>

          <div v-if="shown" class="bg-white rounded-lg p-3">
            <DosageForm
              ref="form"
              :key="shown.id"
              v-model="formData"
              :product="{id: shown.product_id, name: shown.label}"
              hide-duration
              hide-custom-frequency
            />
          </div>

          <div class="bg-white rounded-lg p-3 space-y-2">
            <label class="text-sm font-semibold" for="medication-checkup-dosage-comment">
              Durée / conditions
            </label>
            <Textarea
              id="medication-checkup-dosage-comment"
              v-model="comment"
              placeholder="ex. traitement chronique, max 3 g/j, pendant les repas…"
            />
          </div>

          <p class="rounded-lg bg-primary-lightest px-3 py-2 text-sm text-primary">
            <span class="font-semibold">Aperçu :</span> {{ preview }}
          </p>
        </div>
      </div>

      <div class="flex justify-end items-center gap-3 px-5 py-3 border-t border-gray-light">
        <BaseButton variant="text" @click="emit('close')">
          Annuler
        </BaseButton>
        <BaseButton primary @click="save">
          Enregistrer
        </BaseButton>
      </div>
    </DialogContent>
  </Dialog>
</template>
